// Contrato das ações destrutivas do backend: a frase digitada é conferida no
// servidor (security.go) e nunca é devolvida por nenhuma resposta, senão a
// confirmação seria o eco do que a chamada anterior entregou. Manter em sincronia
// com destructiveConfirm/clearCyclesConfirm do backend.
export const CLEAR_CYCLES_CONFIRM = 'APAGAR CICLOS'
export const CLEAR_HOLES_CONFIRM = 'APAGAR FUROS'

export const DEBUG_CONFIRM = {
  'purge-all': 'APAGAR TUDO',
  'delete-iot': 'APAGAR IOT',
  'delete-broker-user': 'APAGAR BROKER',
  'delete-netbird': 'APAGAR NETBIRD',
  'cleanup-secret-assets': 'APAGAR SEGREDOS',
}

// destructiveDisabled: o 403 dessas rotas significa ambiente com as ações
// destrutivas desligadas (ENABLE_DESTRUCTIVE_DEBUG), que é resposta legítima e
// não falha de rede. Desde o RBAC, 403 também é "o seu perfil não permite": os dois
// casos são distinguidos pelo corpo, porque a mensagem que o operador precisa ler é
// diferente ("ligue o flag" vs "peça a um admin").
export async function destructiveDisabled(res) {
  if (!res || res.status !== 403) return false
  const body = await res.clone().text().catch(() => '')
  return !/perfil/i.test(body)
}

// forbiddenByRole: 403 que veio do RBAC, não do flag de ambiente.
export async function forbiddenByRole(res) {
  if (!res || res.status !== 403) return false
  const body = await res.clone().text().catch(() => '')
  return /perfil/i.test(body)
}

// apiError levanta o motivo que o backend escreveu no corpo ({"error": "..."}), em vez
// do "HTTP 409" que não diz nada ao despachante. O 409 (versão desatualizada, registo
// referenciado) e o 404 (registo apagado noutra sessão) são erros de NEGÓCIO: a mensagem
// deles é a única coisa que explica por que a gravação não passou.
export async function apiError(res) {
  const body = await res.text().catch(() => '')
  let msg = body
  try {
    const j = JSON.parse(body)
    if (j && typeof j.error === 'string') msg = j.error
  } catch { /* corpo não-JSON: usa o texto cru */ }
  return new Error(msg.trim() || `HTTP ${res.status}`)
}

// okJSON: pedido que devolve JSON e levanta o erro do backend quando não passa.
export async function okJSON(url, init) {
  const res = await fetch(url, init)
  if (!res.ok) throw await apiError(res)
  return res.json().catch(() => null)
}

// ---- Camada de autenticação no fetch ------------------------------------------------
//
// São 71 chamadas de fetch espalhadas por 39 arquivos, todas com URL relativa da própria
// origem. O cookie de sessão viaja sozinho nelas; o que falta é o header anti-CSRF nos
// métodos que gravam e um lugar único para reagir ao 401. Migrar os 70 pontos à mão
// seria caro e fácil de esquecer um, então a instalação é feita uma vez sobre o
// window.fetch, no arranque (main.jsx).

let csrfToken = null
let onUnauthorized = null

export function setCsrfToken(t) { csrfToken = t || null }
export function setUnauthorizedHandler(fn) { onUnauthorized = fn }

const MUTATING = new Set(['POST', 'PUT', 'PATCH', 'DELETE'])

// A página de impressão roda dentro do Chromium do Gotenberg, que não tem sessão: ela
// se autentica com o token de relatório na query. O wrapper não pode reagir ao 401 ali,
// senão a página nunca sinalizaria __REPORT_READY__ e o PDF sairia por timeout.
function reportToken() {
  try {
    const q = new URLSearchParams(window.location.search)
    return q.get('report') === '1' ? q.get('rt') : null
  } catch { return null }
}

export function installFetchAuth() {
  // Idempotente: o HMR do Vite reavalia o módulo em dev, e sem esta guarda cada edição
  // encadearia mais um wrapper sobre o anterior.
  if (window.__fetchAuthInstalled) return
  window.__fetchAuthInstalled = true

  const original = window.fetch.bind(window)
  const rt = reportToken()

  window.fetch = async (input, init = {}) => {
    // Resolve contra a origem para cobrir os três formatos que o fetch aceita: string
    // relativa, URL absoluta e objeto Request. Comparar `startsWith('/api/')` na string
    // crua deixaria os dois últimos passarem sem o header anti-CSRF, e a falha seria um
    // 403 sem pista nenhuma.
    const req = input instanceof Request ? input : null
    let target
    try {
      target = new URL(req ? req.url : String(input), window.location.origin)
    } catch {
      return original(input, init)
    }
    const isApi = target.origin === window.location.origin &&
      (target.pathname.startsWith('/api/') || target.pathname === '/events')
    if (!isApi) return original(input, init)

    const method = (init.method || req?.method || 'GET').toUpperCase()
    // Parte dos headers do Request (quando houver) e deixa o init sobrescrever, que é a
    // semântica do fetch. Construir só a partir do init apagaria os do Request.
    const headers = new Headers(req ? req.headers : (init.headers || {}))
    if (req && init.headers) new Headers(init.headers).forEach((v, k) => headers.set(k, v))
    if (MUTATING.has(method) && csrfToken) headers.set('X-CSRF-Token', csrfToken)
    if (rt) headers.set('X-Report-Token', rt)

    const res = await original(input, { ...init, headers, credentials: init.credentials || 'same-origin' })
    if (res.status === 401 && !rt && onUnauthorized) onUnauthorized()
    return res
  }
}
